/**
 * @file MagicLinkPage.tsx
 * @brief `/auth/magic?token=…`: the one-tap link in billing emails ("Add a
 *        card", "See plans"). Signs the user in and opens the Billing screen
 *        the link names; a used or expired link says so with a sign-in button.
 *
 * @details
 * BACKEND A6 (18-10-2026): POST /api/auth/magic/verify { token } → the same
 * payload as /auth/login plus `redirect` (an /app/ path), or 410
 * MAGIC_LINK_EXPIRED. Links are single use, so the verify call is shared per
 * token: React StrictMode (dev) runs the effect twice, and a second call would
 * find the token already spent. Outside RedirectIfAuthenticated on purpose:
 * the link signs in as ITS user even if someone else is signed in here.
 * After "This link has expired", Sign in returns to the same Billing screen.
 * Two-step verification (B31, 25-10-2026): an account with 2FA on (or
 * required) gets the code / setup-only screen here, then the link's page.
 * B33 (26-10-2026): the code answer carries the link's page (`redirect`),
 * so the person lands on the exact Billing screen; an older backend sends
 * none, and they land on Plan (or their home, outside /app). The page is
 * never sent to /2fa/verify: the server keeps it inside the pending token.
 * W27 (26-10-2026): while the link is checked the page says only "Checking
 * your link…"; the panel loader starts once the server signs the user in.
 *
 * @author DES
 * @date 18-10-2026
 */

import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { BrandIcon } from "@/components/brand/BrandIcon";
import { Button } from "@/components/ui/Button";
import { ApiClientError } from "@/shared/api/client";
import { useAuth } from "@/shared/auth/auth-context";
import { allowedPathPrefixForUser, homePathForUser } from "@/shared/auth/homePath";
import { TwoFactorSignInStep } from "@/features/auth/twoFactor/TwoFactorSteps";
import type { AuthUser } from "@/shared/types/api";
import { MAGIC_FALLBACK, magicFailureKind, pageAfterTwoFactor, safeMagicRedirect } from "./magicRedirect";

type Outcome = { kind: "ok"; to: string } | { kind: "expired" | "blocked" | "twoFactor" | "error" };

/** One verify per token for the life of the page (StrictMode double effects). */
const inFlight = new Map<string, Promise<Outcome>>();

export function MagicLinkPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const { loginWithMagicLink, twoFactorChallenge } = useAuth();
  const navigate = useNavigate();
  const [outcome, setOutcome] = useState<Outcome | null>(token ? null : { kind: "expired" });

  useEffect(() => {
    if (!token) return;
    let alive = true;
    let p = inFlight.get(token);
    if (!p) {
      p = loginWithMagicLink(token)
        .then(({ redirect }): Outcome => ({ kind: "ok", to: safeMagicRedirect(redirect) }))
        .catch((err): Outcome => ({
          kind: err instanceof ApiClientError ? magicFailureKind(err.status, err.code) : "error",
        }));
      inFlight.set(token, p);
    }
    void p.then((o) => {
      if (!alive) return;
      if (o.kind === "ok") navigate(o.to, { replace: true });
      else setOutcome(o);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per token
  }, [token]);

  const signInState = { from: { pathname: MAGIC_FALLBACK } };

  // After the code: the link's page (B33), else Plan if this account has one, else its home.
  const afterTwoFactor = (user: AuthUser, redirect: string | null) => {
    navigate(pageAfterTwoFactor(redirect, allowedPathPrefixForUser(user), homePathForUser(user)), { replace: true });
  };

  if (outcome?.kind === "twoFactor" && twoFactorChallenge) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <div className="w-full max-w-sm">
          <TwoFactorSignInStep onSignedIn={afterTwoFactor} onCancel={() => navigate("/login", { replace: true })} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="max-w-sm text-center">
        <BrandIcon className="mx-auto h-10 w-10" />
        {!outcome ? (
          <>
            {/* No spinner, no "Signing you in…" (W27): the answer may be the
                code screen, and the panel loader only follows a yes. */}
            <p className="mt-6 text-sm text-muted-foreground">Checking your link…</p>
          </>
        ) : outcome.kind === "blocked" ? (
          <>
            <h1 className="mt-6 text-2xl font-semibold tracking-tight">This account can't sign in</h1>
            <p className="mt-3 text-sm text-muted-foreground">
              It may be suspended. Contact support if you think this is wrong.
            </p>
            <Button asChild className="mt-6">
              <Link to="/login">Go to sign in</Link>
            </Button>
          </>
        ) : (
          <>
            <h1 className="mt-6 text-2xl font-semibold tracking-tight">
              {outcome.kind === "error" ? "We couldn't sign you in" : "This link has expired"}
            </h1>
            <p className="mt-3 text-sm text-muted-foreground">
              {outcome.kind === "error"
                ? "Something went wrong. Sign in to continue."
                : "Links from our emails work once, for 15 minutes. Sign in to continue."}
            </p>
            <Button asChild className="mt-6">
              <Link to="/login" state={signInState}>
                Sign in
              </Link>
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
